import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Button, Checkbox } from '@ergeon/core-components';
import { getHelpSearchLocation } from '@ergeon/help-components';
import { MagnifyingGlassIcon } from '@ergeon/icons/hero/mini';

const FIELD_CLASSES = classNames(
  'flex w-full items-center gap-2 rounded-xl border border-gray-300 bg-white py-1.5 pl-4 pr-1.5 shadow-xs',
  'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500',
  'dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-blue-300 dark:focus-within:ring-blue-300',
);

const INPUT_CLASSES = classNames(
  'h-10 min-w-0 flex-1 bg-transparent paragraph-1 text-gray-900 outline-hidden',
  'placeholder:text-gray-600 dark:text-white dark:placeholder:text-gray-400',
);

type WikiSearchFormProps = {
  initialQuery?: string;
  localNodeKey?: string;
  localTitle?: string;
};

const getScopeLabel = (localNodeKey: string, localTitle?: string) => {
  if (localTitle) {
    return `Only under “${localTitle}”`;
  }
  return `Only under page #${localNodeKey}`;
};

const getSearchScope = (isLocal: boolean, localNodeKey?: string) => {
  if (isLocal) {
    return localNodeKey;
  }
  return undefined;
};

const WikiSearchForm = ({ initialQuery = '', localNodeKey, localTitle }: WikiSearchFormProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [isLocal, setIsLocal] = useState(Boolean(localNodeKey));

  useEffect(function followTheSearchedQuery() {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(function followTheSearchedScope() {
    setIsLocal(Boolean(localNodeKey));
  }, [localNodeKey]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return;
    }
    navigate(getHelpSearchLocation(trimmedQuery, { localNodeKey: getSearchScope(isLocal, localNodeKey) }));
  }, [isLocal, localNodeKey, navigate, query]);

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit} role="search">
      <div className={FIELD_CLASSES}>
        <MagnifyingGlassIcon aria-hidden="true" className="size-5 shrink-0 text-gray-600 dark:text-gray-400" />
        <input
          aria-label="Search the wiki"
          className={INPUT_CLASSES}
          enterKeyHint="search"
          name="q"
          onChange={handleChange}
          placeholder="Search the wiki"
          type="search"
          value={query} />
        <Button flavor="primary" type="submit">Search</Button>
      </div>
      {localNodeKey && (
        <Checkbox checked={isLocal} onClick={setIsLocal}>
          {getScopeLabel(localNodeKey, localTitle)}
        </Checkbox>
      )}
    </form>
  );
};

export default WikiSearchForm;
